/**
 * Audit trail — ADR-001 §6.5. Every write to an audited table is recorded by a database trigger
 * (who, when, before, after, changed columns). The API only reads it.
 *
 * Masking: for callers without `revenue.read`, revenue columns (`annual_revenue`, `mrr`, `plan_value`,
 * `value` and their `_lakh` forms) are removed from `before`, `after` and `changed`. An entry whose
 * only change was a revenue column is omitted entirely for those callers.
 */
import { z } from 'zod';
import { PageQuery, Timestamp, Uuid } from './common.js';
import { UserRef } from './users.js';
/** Tables the audit trigger is attached to (§6.5). Step 1 writes users, clients and client_spocs. */
export const AUDITED_TABLES = [
    'users',
    'clients',
    'client_spocs',
    'client_contacts',
    'client_email_domains',
    'client_products',
    'tasks',
    'task_slips',
    'task_blocks',
    'task_closures',
    'task_assignments',
    'dropped_items',
    'day_plans',
    'day_status',
    'client_notes',
    'escalation_notes',
    'request_messages',
    'item_closures',
    'holiday_calendars',
    'holiday_days',
    'client_requests',
    'internal_requests',
    'escalations',
    'client_tasks',
    'change_requests',
    'meetings',
    'documents',
    'opportunities',
    'held_items',
    'checklist_templates',
    'client_checklist_items',
    'timesheet_days',
    'timesheet_entries',
    'settings',
    'engagement_log',
    'usage_log_entries',
    'ideas',
    // Email + password sign-in (db/policies/0058): written by its own functions, never with the hash.
    'user_passwords',
    'password_sign_ins',
    // Personal capture links (db/policies/0061): who issued / rotated / removed whose link, never the token or its hash.
    'capture_tokens',
];
export const AuditTable = z.enum(AUDITED_TABLES).meta({ id: 'AuditTable' });
export const AuditOp = z.enum(['INSERT', 'UPDATE', 'DELETE']).meta({ id: 'AuditOp' });
/** `user` = a signed-in request; `job` = the worker; `migration` = schema/seed scripts; `system` = an operator's one-off command (the first-founder bootstrap, seed:purge). */
export const ActorKind = z.enum(['user', 'job', 'migration', 'system']).meta({ id: 'ActorKind' });
const RowImage = z.record(z.string(), z.unknown());
/** Revenue fields in every form they can take in a row image: the `_lakh` API names and the DB columns. */
export const REVENUE_COLUMN_NAMES = [
    'mrr', 'plan_value', 'annual_revenue', 'value', 'revenue_at_risk',
    'mrr_lakh', 'plan_value_lakh', 'annual_revenue_lakh', 'value_lakh', 'revenue_at_risk_lakh',
];
const isRevenueName = (k) => REVENUE_COLUMN_NAMES.includes(k);
const noRevenue = 'Revenue fields are never sent to this caller';
/** A row image for callers without revenue.read: any revenue key fails validation. */
const RowImageMember = RowImage.refine((o) => !Object.keys(o).some(isRevenueName), { message: noRevenue });
export const AuditEntry = z
    .object({
    /** bigserial, sent as a string so it never loses precision in JavaScript. */
    id: z.string().regex(/^\d+$/),
    at: Timestamp,
    /** The real person who made the write; `null` for job and migration writes. */
    actor: UserRef.nullable(),
    /** Set when the actor was acting as someone else (operator decision 18): the acting-as user. */
    on_behalf_of: UserRef.nullable(),
    actor_kind: ActorKind,
    /** Joins the API log line and the error-tracker event for the same request. */
    request_id: z.string().nullable(),
    table_name: AuditTable,
    row_id: z.string(),
    op: AuditOp,
    /** Column names that actually differ. Empty for INSERT and DELETE. */
    changed: z.array(z.string()),
    /** Row before the write; `null` on INSERT. */
    before: RowImage.nullable(),
    /** Row after the write; `null` on DELETE. */
    after: RowImage.nullable(),
})
    .meta({ id: 'AuditEntry' });
/** AuditEntry for callers without revenue.read: before/after/changed may not carry any revenue name. */
export const AuditEntryMember = AuditEntry.extend({
    changed: z.array(z.string().refine((k) => !isRevenueName(k), { message: noRevenue })),
    before: RowImageMember.nullable(),
    after: RowImageMember.nullable(),
}).meta({ id: 'AuditEntryMember' });
/** GET /audit — managers only. Newest first. */
export const ListAuditQuery = PageQuery.extend({
    table: AuditTable.optional(),
    /** Requires `table`. */
    row_id: z.string().min(1).max(64).optional(),
    actor_id: Uuid.optional(),
    /** Inclusive lower bound. */
    from: Timestamp.optional(),
    /** Exclusive upper bound. */
    to: Timestamp.optional(),
    op: AuditOp.optional(),
});
export const AuditPage = z
    .object({ data: z.array(AuditEntry), next_cursor: z.string().nullable() })
    .meta({ id: 'AuditPage' });
export const AuditPageMember = z
    .object({ data: z.array(AuditEntryMember), next_cursor: z.string().nullable() })
    .meta({ id: 'AuditPageMember' });
/**
 * GET /audit/{table}/{row_id} — the "History" tab of every side panel. Allowed for managers and for
 * anyone who may read that row; a row the caller may not read is `404 not_found`.
 */
export const RowHistoryParams = z.object({
    table: AuditTable,
    row_id: z.string().min(1).max(64),
});
export const RowHistoryQuery = PageQuery;
//# sourceMappingURL=audit.js.map